import React from "react";
// The Font Awesome library is assumed to be available in the environment
// via a link tag in the main HTML file.

const ElectronicSignatureConsent: React.FC = () => {
  return (
    <div className="bg-gray-100 p-4 sm:p-8 md:p-12 lg:p-16">
      <div className="bg-white max-w-4xl mx-auto rounded-xl shadow-2xl p-6 sm:p-8 md:p-12 lg:p-16">
        {/* Title and Introduction Section */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-2 leading-tight">Waivera Consent to Use Electronic Signatures</h1>
        <p className="text-gray-500 text-sm mb-8 font-medium">Last Updated: June 3, 2025</p>

        <div className="mb-12 text-gray-700 space-y-6 text-sm sm:text-base">
          <p>
            Waivera is an electronic signature platform designed to facilitate secure and legally
            binding digital transactions. It allows users, including Medicaid case managers,
            waiver clients, in-home providers, caretakers, and state representatives, to sign,
            send, and manage documents electronically without the need for physical
            paperwork.
          </p>
          <p>
            Waivera complies with key federal and state laws governing electronic signatures,
            including the Electronic Signatures in Global and National Commerce Act (ESIGN
            Act) and the Uniform Electronic Transactions Act (UETA). These laws establish the
            legality of electronic signatures across the United States. Additionally, Waivera
            adheres to the specific regulations and requirements for Louisiana and Florida,
            ensuring compliance with state laws governing digital transactions, Medicaid-
            related documentation, and healthcare data protection.
          </p>
        </div>

        {/* Scope of Consent Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-file-contract text-green-500"></i> Scope of Consent
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              By using Waivera, users agree that all electronic signatures executed through the
              platform carry the same legal effect as traditional handwritten signatures. This
              consent applies to all documents signed, submitted, or processed within Waivera,
              including agreements, forms, and other legally binding transactions.
            </p>
            <p>
              Users acknowledge and accept that electronic signatures provided through Waivera
              are legally valid and enforceable. By granting this consent, users waive any right to
              dispute the legality, authenticity, or enforceability of electronically signed
              documents solely on the basis that they were signed in digital form.
            </p>
          </div>
        </div>

        {/* Right to Withdraw Consent Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-hand-paper text-orange-500"></i> Right to Withdraw Consent
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              Users have the right to withdraw their consent to use electronic signatures at any
              time. To do so, they must submit a written request to Waivera at
              <a href="mailto:notifications@waivera.com" className="text-blue-600 hover:underline"> notifications@waivera.com</a> or the company’s registered address. The withdrawal of consent will take effect
              once the request is processed and confirmed by Waivera
            </p>
            <p>
              However, withdrawing consent may limit or restrict access to certain features of
              Waivera, including the ability to sign, send, or manage documents electronically.
              Users who choose to withdraw consent may be required to complete transactions
              using alternative methods, such as handwritten signatures or physical document
              submissions.
            </p>
            <p>
              Additionally, users may request paper copies of any electronically signed
              documents by contacting Waivera A reasonable processing time may be required
              to fulfill such requests, and in some cases, additional fees may apply for document
              retrieval and mailing services.
            </p>
          </div>
        </div>

        {/* Access and Retention Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-database text-purple-500"></i> Access and Retention
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              Users confirm that they have the necessary hardware, software, and internet access
              to review, sign, and retain electronic records through Waivera. This includes a
              compatible device (such as a computer, tablet, or smartphone) with an updated
              web browser and sufficient storage to save copies of electronically signed
              documents.
            </p>
            <p>
              All signed documents are securely stored within Waivera’s system, ensuring
              accessibility and compliance with applicable laws. Users have the option to
              download and retain copies of their signed agreements at any time through the
              platform. Waivera also provides a secure audit trail that tracks key transaction
              details, such as timestamps and signer authentication.
            </p>
            <p>
              If users require copies of past agreements that are no longer accessible within their
              account, they can submit a request to Waivera via
              <a href="mailto:notifications@waivera.com" className="text-blue-600 hover:underline"> notifications@waivera.com</a>. Depending
              on the request, additional verification may be required to ensure data security and
              compliance with HIPAA and other regulations. In some cases, a processing fee may
              apply for document retrieval and distribution.
            </p>
          </div>
        </div>

        {/* Electronic Signature Process Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-file-signature text-red-500"></i> Electronic Signature Process
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              Waivera streamlines the electronic signature workflow to ensure efficiency,
              security, and legal compliance. The process follows these key steps:
            </p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Document Preparation:</strong> Case Managers or authorized users select prefilled documents from the Waivera platform. They can then place signature blocks where signatures, initials, or other required inputs are needed.</li>
              <li><strong>Sending for Signature:</strong> Once the document is prepared, the Case Manager enters the email addresses of the recipients and sends the document for signing. Recipients are notified via email with a secure link directing them to the document.</li>
              <li><strong>Review and Signing:</strong> Recipients access the document through the secure link, where they can review its contents before applying their electronic signature. The platform ensures clarity by displaying a confirmation message that acknowledges the legal binding nature of their signature before finalizing the process.</li>
              <li><strong>Audit Trail Generation:</strong> Every electronically signed document is accompanied by an audit trail, which records critical transaction details.</li>
              <li><strong>Secure Storage and Notifications:</strong> Once all required parties have signed, Waivera automatically stores the completed document in the client’s case file. Notifications are sent to all involved parties confirming the document’s completion and availability for download or future reference.</li>
            </ul>
          </div>
        </div>

        {/* Legal Compliance and Enforceability Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-gavel text-gray-500"></i> Legal Compliance and Enforceability
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              Waivera ensures that all electronically signed documents are legally binding and
              enforceable under applicable federal and state laws. By using the platform, users
              acknowledge that electronic signatures executed through Waivera carry the same
              legal effect as handwritten signatures, in accordance with the Electronic Signatures
              in Global and National Commerce Act (ESIGN Act) and the Uniform Electronic
              Transactions Act (UETA).
            </p>
            <p>
              Additionally, Waivera is designed to meet compliance requirements specific to the
              healthcare industry, including HIPAA (Health Insurance Portability and
              Accountability Act) and Medicaid regulations. This means that all sensitive
              information collected, stored, and processed through the platform is handled in
              accordance with strict data security and privacy standards.
            </p>
            <p>
              Waivera also incorporates safeguards to ensure compliance with state-specific laws
              in Louisiana and Florida, where Medicaid regulations and electronic signature
              policies may have additional requirements. By using Waivera, users confirm their
              understanding that all signed documents are valid, enforceable, and compliant with
              the governing legal frameworks.
            </p>
          </div>
        </div>

        {/* Amendments to This Consent Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-edit text-blue-500"></i> Amendments to This Consent
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              Waivera reserves the right to update, modify, or amend the terms of this Consent
              to Use Electronic Signatures at any time. Any changes will be effective upon posting
              the revised terms within Waivera or providing direct notification to users, as
              required by law.
            </p>
            <p>
              Users are encouraged to review the consent terms periodically to stay informed of
              any updates. Continued use of Waivera after any modifications to this consent will
              be considered acceptance of the revised terms. If a user does not agree to the
              changes, they must discontinue using Waivera and may withdraw their consent as
              outlined in the Right to Withdraw Consent section.
            </p>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <i className="fas fa-envelope text-pink-500"></i> Contact Information
          </h2>
          <div className="text-gray-700 space-y-4 text-sm sm:text-base">
            <p>
              For any questions, concerns, or requests regarding this consent, users may contact
              Waivera at:
            </p>
            <ul className="list-none space-y-2">
              <li><strong>Email:</strong> notifications@waivera.com</li>
              <li><strong>Mailing Address:</strong> Waivera, 10201 Park Rowe Avenue, Unit 2324, Baton Rouge, LA 70810</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElectronicSignatureConsent;
